import storeManager from '../../store/StoreManager';
import sessionConstants from '../../constants/SessionConstants';
import settingsReader from './SettingsReader';
import candidateBlockageReason from '../../constants/CandidateBlockageReason';
import uiReader from './uiReader';
import assessmentReader from './AssessmentReader';

const sessionReader = {
    printParamNames: {
        TYPE: 'type',
        LAYOUT: 'layout',
        SHOW_COVER: 'show_cover',
        SHOW_DOCUMENTS: 'show_documents',
        SHOW_MANUAL_QUESTIONS_ONLY: 'show_manual_questions_only',
        SHOW_CORRECT_ANSWERS: 'show_correct_answers',
        SHOW_MAX_MARKS: 'show_max_marks',
        SHOW_PDF_PANELS: 'show_pdf_panels',
        SHOW_TABLE_OF_CONTENTS: 'show_table_of_contents',
        INSERT_PAGE_BREAK_BEFORE_QUESTION: 'insert_page_break_before_question',
        PRINT_CREATED_BY: 'print_created_by', // sent from Phantom
        EXCLUDE_PDF: 'exclude_pdf',
        HIDE_INTERACTIONS_NOT_USEFUL_ON_PAPER: 'hide_interactions_not_useful_on_paper',
        SHOW_PREVIEW_WITH_FEEDBACK: 'show_preview_with_feedback',
        INSERT_PAGE_BREAK_ON_NEW_SECTION: 'insert_page_break_on_new_section',
    },

    layoutParamNames: {
        SHOW_CHECK_ANSWER_BUTTON: 'show_check_answer_button',
        SHOW_CORRECT_ANSWERS: 'show_correct_answers',
        SHOW_ANSWER_KEYS: 'show_answer_keys',
        SHOW_AUDIO_SCRIPT: 'show_audio_script',
        HIDE_RESPONSE_AREA: 'hide_response_area',
    },

    isContextEmpty(context = storeManager.getState().session.context) {
        return context === undefined;
    },

    isContextPreview(context = storeManager.getState().session.context) {
        return context === sessionConstants.CONTEXT_PREVIEW;
    },

    isContextReadinessCheck(state = storeManager.getState()) {
        const { context, requestParameterIds } = state.session;
        if (requestParameterIds.childContext) {
            return requestParameterIds.childContext === sessionConstants.CONTEXT_READINESS_CHECK;
        }
        return context === sessionConstants.CONTEXT_READINESS_CHECK;
    },

    isContextPreviewAssessmentRun(context = storeManager.getState().session.context) {
        return this.isContextPreview(context) && this.getAssessmentRunId();
    },

    isContextExam(context = storeManager.getState().session.context) {
        return context === sessionConstants.CONTEXT_EXAM;
    },

    isContextAnyViewDeliveryType(context = storeManager.getState().session.context) {
        return this.isContextViewDelivery(context)
            || this.isContextViewGradingQuestionDelivery(context)
            || this.isContextViewOwnDelivery(context)
            || this.isContextViewDeliveryForAnnotationPdf(context);
    },

    isContextViewDelivery(context = storeManager.getState().session.context) {
        return context === sessionConstants.CONTEXT_VIEW_DELIVERY;
    },

    isContextViewDeliveryForAnnotationPdf(context = storeManager.getState().session.context) {
        return context === sessionConstants.CONTEXT_VIEW_DELIVERY_FOR_ANNOTATION_PDF;
    },

    /**
     Note this function also returns true for admins (e.g. Planner/Grader) using the "View report as Candidate" feature
     */
    isContextViewOwnDelivery(context = storeManager.getState().session.context) {
        return context === sessionConstants.CONTEXT_VIEW_OWN_DELIVERY;
    },

    isContextViewGradingQuestionDelivery(context = storeManager.getState().session.context) {
        return context === sessionConstants.CONTEXT_VIEW_GRADING_QUESTION_DELIVERY;
    },

    isContextViewGradingQuestionPreview(context = storeManager.getState().session.context) {
        return context === sessionConstants.CONTEXT_VIEW_GRADING_QUESTION_PREVIEW;
    },

    isContextViewGradingQuestion(context = storeManager.getState().session.context) {
        return this.isContextViewGradingQuestionDelivery(context)
            || this.isContextViewGradingQuestionPreview(context);
    },

    isContextThumbnail(context = storeManager.getState().session.context) {
        return context === sessionConstants.CONTEXT_THUMBNAIL;
    },

    isContextPreviewAudioScript(context = storeManager.getState().session.context) {
        return context === sessionConstants.CONTEXT_PREVIEW_AUDIO_SCRIPT;
    },

    isModeQuestion(mode = storeManager.getState().session.mode) {
        return mode === sessionConstants.MODE_QUESTION;
    },

    isModeQuestionInPrintView() {
        return this.isViewMediaPrint() && this.isModeQuestion();
    },

    isModeSection(mode = storeManager.getState().session.mode) {
        return mode === sessionConstants.MODE_SECTION;
    },

    isModeStructure(mode = storeManager.getState().session.mode) {
        return mode === sessionConstants.MODE_STRUCTURE;
    },

    isModeQuestionSet(mode = storeManager.getState().session.mode) {
        return mode === sessionConstants.MODE_QUESTION_SET;
    },

    isViewAsCandidate(viewAs = storeManager.getState().session.viewAs) {
        return (this.isContextPreview() || this.isContextViewGradingQuestionPreview())
            && viewAs === sessionConstants.VIEW_AS_CANDIDATE;
    },

    isViewMediaPrint(viewMedia = storeManager.getState().session.viewMedia) {
        return viewMedia === sessionConstants.VIEW_MEDIA_PRINT;
    },

    isViewMediaPrintOrPdf(viewMedia = storeManager.getState().session.viewMedia) {
        return viewMedia === sessionConstants.VIEW_MEDIA_PRINT
                || viewMedia === sessionConstants.VIEW_MEDIA_PDF;
    },

    isViewMediaImage(viewMedia = storeManager.getState().session.viewMedia) {
        return viewMedia === sessionConstants.VIEW_MEDIA_IMAGE;
    },

    isViewMediaPdf(viewMedia = storeManager.getState().session.viewMedia) {
        return viewMedia === sessionConstants.VIEW_MEDIA_PDF;
    },

    isViewMediaScreen(viewMedia = storeManager.getState().session.viewMedia) {
        return viewMedia === sessionConstants.VIEW_MEDIA_SCREEN || viewMedia === undefined;
    },

    isResumed() {
        const state = storeManager.getState();
        return state.session.resumed === true;
    },

    isChildResumed() {
        const state = storeManager.getState();
        return state.session.childResumed === true;
    },

    isResumedTestAfterPassword() {
        const state = storeManager.getState();
        return state.session.resumedTestAfterPassword === true;
    },

    isOpenForReDelivery() {
        return !!this.getSessionInfoValue('openForReDelivery');
    },

    isRequiringPasswordForResume() {
        return (
            this.userHasLockDown()
            || this.requireOpenBrowserInvigilatorPassword()
            || this.testInVDIWithInvigilatorPassword()
        )
        && this.isResumed()
        && !this.isResumedTestAfterPassword()
        && !this.isDemoExam()
        && !settingsReader.isInvigilatorPasswordToResumeTestDisabled();
    },

    // IB-1558 - needed to check if test has lockdown or requires invigilator password for resume,
    // used to check if a test requires invigilator password,
    // not taking into account if the password has a value or not (which the testUsesInvigilatorPassword method checks)
    isTestConfiguredToRequireInvigilatorPassword() {
        const requireInvigilatorPassword = this.requireOpenBrowserInvigilatorPassword()
            || this.requireLockDownChromeKiosk()
            || this.requireLockDownBrowser();

        return (this.userHasLockDown() || requireInvigilatorPassword) && !this.isDemoExam();
    },

    isOneWayNavigationSupportedContext() {
        return this.isContextExam() || (this.isContextPreview() && uiReader.isOwnPreviewEnabled());
    },

    getAnnotationContext() {
        const state = storeManager.getState();
        return state.session.annotationContext;
    },

    getQuestionSetIdentifier() {
        const state = storeManager.getState();
        if (state.session.childTestIdentifier) {
            return state.session.childTestIdentifier;
        }
        return state.session.questionSetIdentifier;
    },

    getAcademyMarketplaceId() {
        return storeManager.getState().session.requestParameterIds.academyMarketplaceId;
    },

    getAssessmentRunId(useParent) {
        const state = storeManager.getState();
        const { requestParameterIds } = state.session;
        if (!useParent && requestParameterIds.childAssessmentRunId) {
            return requestParameterIds.childAssessmentRunId;
        }
        return requestParameterIds?.assessmentRunId;
    },

    getContentItemId() {
        const state = storeManager.getState();
        return state.session.requestParameterIds.contentItemId;
    },

    getContentRevisionId() {
        const state = storeManager.getState();
        return state.session.requestParameterIds.contentRevisionId;
    },

    getContentVersionId() {
        const state = storeManager.getState();
        return state.session.requestParameterIds.contentVersionId;
    },

    getChildAssessmentRunId() {
        const state = storeManager.getState();
        return state.session.requestParameterIds.childAssessmentRunId;
    },

    isActiveChildTestSession() {
        return this.isParentTest() && this.getChildAssessmentRunId();
    },

    getChildContentRevisionId() {
        const state = storeManager.getState();
        return state.session.requestParameterIds.childContentRevisionId;
    },

    getContext() {
        const state = storeManager.getState();
        return state.session.context;
    },

    getMode() {
        const state = storeManager.getState();
        return state.session.mode;
    },

    getPartyId() {
        const state = storeManager.getState();
        return state.session.requestParameterIds.partyId;
    },

    getLanguage() {
        const state = storeManager.getState();
        return state.session.language;
    },

    getLanguageId() {
        const state = storeManager.getState();
        return state.session.requestParameterIds.languageId;
    },

    getLocale() {
        const state = storeManager.getState();
        return state.session.requestParameterIds.locale;
    },

    getRequestingPartyId() {
        const state = storeManager.getState();
        return state.session.requestParameterIds.requestingPartyId;
    },

    getSessionInfoValue(key, useParent = false) {
        const state = storeManager.getState();
        const { childSessionInfo, sessionInfo } = state.session;

        const currentSessionInfo = childSessionInfo && !useParent ? childSessionInfo : sessionInfo;

        if (typeof currentSessionInfo === 'object' && currentSessionInfo[key] !== undefined) {
            return currentSessionInfo[key];
        }
        return undefined;
    },

    getCandidateId() {
        return this.getSessionInfoValue('candidateId') || '';
    },

    showEndTimePostPrimaryEndTime() {
        return this.getSessionInfoValue('showEndTimePostPrimaryEndTime') || false;
    },

    getUserInfo() {
        const firstName = this.getSessionInfoValue('firstName');
        const lastName = this.getSessionInfoValue('lastName');
        const socialSecurityNumber = this.getSessionInfoValue('socialSecurityNumber');

        if (firstName || lastName || socialSecurityNumber) {
            return { firstName, lastName, socialSecurityNumber };
        }

        return undefined;
    },

    getUserId() {
        return this.getSessionInfoValue('userId') || -1;
    },

    getFileSaveKey() {
        return this.getSessionInfoValue('fileSaveKey');
    },

    getFileSaveId() {
        return this.getSessionInfoValue('fileSaveId');
    },

    getLoginAttemptId() {
        return this.getSessionInfoValue('loginAttemptId');
    },

    getPushChannels(useParent) {
        return this.getSessionInfoValue('pushChannels', useParent);
    },

    getSignedWebSocketUrl(useParent) {
        return this.getSessionInfoValue('signedWebSocketUrl', useParent);
    },

    getCsrfToken() {
        return window.csrfToken;
    },

    passesLockDownRequirements() {
        const passesLockDownRequirements = this.getSessionInfoValue('passesLockDownRequirements');
        return passesLockDownRequirements !== undefined ? passesLockDownRequirements : true;
    },

    getBlockageReason() {
        return this.getSessionInfoValue('blockageReason');
    },

    getBlockageReasonsForTest() {
        // blockageReason and blockageReasonsForTest are different parameters
        // see details - https://inspera.atlassian.net/wiki/spaces/ITP/pages/84246691/API+StartTest
        return this.getSessionInfoValue('blockageReasonsForTest', true);
    },

    hasEvictionBlockageReasonForTest() {
        const reasons = this.getBlockageReasonsForTest();
        return Array.isArray(reasons) && reasons.includes(candidateBlockageReason.EVICTION);
    },

    hasReadinessCheck() {
        return !!this.getSessionInfoValue('isReadinessCheckEnabled', true);
    },

    getReadinessCheckContentRevisionId() {
        return this.getSessionInfoValue('readinessCheckContentRevisionId', true);
    },

    getReadinessCheckTitle() {
        return this.getSessionInfoValue('readinessCheckTitle', true);
    },

    // isParentTest actually means "isCompositeContext" (thus true when parent test or child test)
    isParentTest() {
        return this.getSessionInfoValue('isParentTest', true) || false;
    },

    isParentTestContextOutsideChildTest() {
        return this.isParentTest() && (!this.getChildAssessmentRunId() || this.isContextReadinessCheck());
    },

    isAutoDeliverHighStakesEnabled() {
        return this.getSessionInfoValue('autoDeliverHighStakesEnabled') || false;
    },

    userHasSeb() {
        return this.getSessionInfoValue('userHasSeb') || false;
    },

    userHasChromeKiosk() {
        return this.getSessionInfoValue('userHasChromeKiosk') || false;
    },

    userHasIEP() {
        return this.getSessionInfoValue('userHasIEP') || false;
    },

    shouldDelegateClipboardClearingToIEP() {
        return this.getSessionInfoValue('shouldDelegateClipboardClearingToIEP') || false;
    },

    shouldDisableCopyPasteInPlayerChatbox() {
        return this.getSessionInfoValue('shouldDisableCopyPasteInPlayerChatbox') || false;
    },

    requireOpenBrowserInvigilatorPassword() {
        return this.getSessionInfoValue('requireOpenBrowserInvigilatorPassword', true) || false;
    },

    testInVDIEnabled() {
        return this.getSessionInfoValue('testInVDIEnabled') || false;
    },

    isReducedRelativeTimeDimensionEnabled() {
        return this.getSessionInfoValue('reducedRelativeTimeDimension') || false;
    },

    testInVDIWithInvigilatorPassword() {
        return this.testInVDIEnabled() && this.testUsesInvigilatorPassword();
    },

    requireLockDownBrowser() {
        return this.getSessionInfoValue('requireLockDownBrowser', true) || false;
    },

    requireLockDownChromeKiosk() {
        return this.getSessionInfoValue('requireLockDownChromeKiosk', true) || false;
    },

    requireTestOverViewExitInvigilatorPassword() {
        return this.getSessionInfoValue('requireTestOverViewExitInvigilatorPassword', true) || false;
    },

    getInvigilatorPassword() {
        return this.getSessionInfoValue('invigilatorPassword', true);
    },

    getStartTime() {
        return this.getSessionInfoValue('startTime');
    },

    isDemoExam() {
        return this.getSessionInfoValue('demoExam') || false;
    },

    isPublicExam() {
        return this.getSessionInfoValue('publicExam') || false;
    },

    isAutoDeliveringEnabled() {
        return this.getSessionInfoValue('autoDeliveringEnabled') || false;
    },

    isAutoDeliveringWithManualEnabled() {
        return this.getSessionInfoValue('autoDeliverWithManualEnabled') || false;
    },

    isRemoteProctoringWebCamEnabled() {
        return this.getSessionInfoValue('remoteProctoringWebCamEnabled') || false;
    },

    isRemoteProctoringMicEnabled() {
        return this.getSessionInfoValue('remoteProctoringMicEnabled') || false;
    },

    isRemoteProctoringRoomscanEnabled() {
        return this.getSessionInfoValue('remoteProctoringRoomScanEnabled') || false;
    },

    supportsLivestream() {
        return this.getSessionInfoValue('watchAndCallEnabled') || false;
    },

    getLiveSessionId() {
        const state = storeManager.getState();
        const { liveSessionId } = state.livestream;
        return liveSessionId;
    },

    supportsTestOverview() {
        return this.isVideoAudioCheckEnabled() || this.isParentTest() || this.hasReadinessCheck();
    },

    /**
     * @returns Number in kbps.
     */
    getLiveProctoringAudioBitrate() {
        return this.getSessionInfoValue('liveProctoringAudioBitrate', true);
    },

    /**
     * @returns Number in kbps.
     */
    getLiveProctoringVideoBitrate() {
        return this.getSessionInfoValue('liveProctoringVideoBitrate', true);
    },

    /**
     * chromeExtensionId is a required parameter when using sendMessage to communicate back to the Chrome App
     * @returns {string}
     */
    getChromeExtensionId() {
        const state = storeManager.getState();
        return state.session.chromeExtensionId;
    },

    userHasLockDown() {
        return this.userHasSeb() || this.userHasChromeKiosk() || this.userHasIEP();
    },

    testUsesInvigilatorPassword() {
        const requireInvigilatorPassword = this.requireOpenBrowserInvigilatorPassword()
            || this.requireLockDownChromeKiosk()
            || this.requireLockDownBrowser();

        return (this.userHasLockDown() || requireInvigilatorPassword)
            && !!this.getInvigilatorPassword()
            && !this.isDemoExam();
    },

    isInvigilatorAuthorized() {
        const state = storeManager.getState();
        return state.session.invigilatorAuthorized;
    },

    getNavigationLock() {
        const state = storeManager.getState();
        return state.session?.navigationLock;
    },

    isDeliverInProgress() {
        const navigationLock = this.getNavigationLock();
        return navigationLock?.reason === sessionConstants.LOCK.DELIVER_IN_PROGRESS;
    },

    hasDelivered() {
        const state = storeManager.getState();
        return state.delivering.hasDelivered;
    },

    isOfflineMode() {
        return !!window.offlineMode; // TODO: Get from state, when we have it there
    },

    isLoginRequired() {
        return this.isOfflineMode();
    },

    isLoggedIn(session = storeManager.getState().session) {
        return session.isLoggedIn;
    },

    isPaused() {
        const state = storeManager.getState();
        return state.session.navigationLock.reason === sessionConstants.LOCK.TEST_PAUSED;
    },

    isCatTriggerInProgress() {
        const state = storeManager.getState();
        return state.session.catTriggerInProgress;
    },

    isAnnotationMode() {
        const state = storeManager.getState();
        return state.session.enableHypothesis;
    },

    isAnnotationsSidebarOpen() {
        const state = storeManager.getState();
        return !!state.session.annotationsSidebarOpen;
    },

    isChatOpen() {
        const state = storeManager.getState();
        return !!state.session.chatSidebarOpen;
    },

    getParam(paramName, defaultValue) {
        const { session } = storeManager.getState();
        return session[paramName] !== undefined ? session[paramName] : defaultValue;
    },

    getPrintParams() {
        const state = storeManager.getState();
        return state.session.printParams;
    },

    getPrintParam(paramName, defaultValue) {
        const params = this.getPrintParams() || {};
        return params[paramName] !== undefined ? params[paramName] : defaultValue;
    },

    getLayoutParams() {
        const state = storeManager.getState();
        return state.session.layoutParams;
    },

    getLayoutParam(paramName, defaultValue) {
        const params = this.getLayoutParams() || {};
        return params[paramName] !== undefined ? params[paramName] : defaultValue;
    },

    shouldDebugTrackers(state = storeManager.getState()) {
        return !!state.session?.debugParams?.debugTrackers;
    },

    shouldDebugSnapshots(state = storeManager.getState()) {
        return !!state.session.debugParams.debugSnapshots;
    },

    shouldDebugTimeZone(state = storeManager.getState()) {
        return !!state.session.debugParams.debugTimeZone;
    },

    isInteractive() {
        return this.isContextThumbnail() // Because thumbnails must look interactive.
            || (this.isViewMediaScreen()
                && (this.isContextExam()
                    || this.isContextPreview()
                        || this.isContextReadinessCheck()));
    },

    isResettableContext() {
        return this.isContextExam() && !this.isContextReadinessCheck();
    },

    questionSetHasAdditionalResources(state = storeManager.getState()) {
        const { settings, assessment, parentTest } = state;
        const sebAdditionalResources = settings.values.lockdown_additional_resources ? JSON.parse(settings.values.lockdown_additional_resources) : [];

        let additionalResources;
        if (parentTest && parentTest.assessment) {
            additionalResources = parentTest.assessment.additionalResources;
        } else {
            additionalResources = assessment.additionalResources;
        }

        return (additionalResources && additionalResources.length > 0)
            || sebAdditionalResources.length > 0;
    },

    questionSetHasRubricsMarking() {
        const { assessment: { sections = [] } } = storeManager.getState();

        return sections.some(({ questions = [] }) => (
            questions.some(question => !!question.hasRubricsMarking)
        ));
    },

    useDynamicLoading(dynamicLoading = storeManager.getState().session.dynamicLoading) {
        return dynamicLoading;
    },

    getDataSetType({ dataSetType, mode } = storeManager.getState().session) {
        return dataSetType || mode;
    },

    isDataSetTypeQuestion({ dataSetType, mode } = storeManager.getState().session) {
        return this.getDataSetType({ dataSetType, mode }) === sessionConstants.MODE_QUESTION;
    },

    isDataSetTypeSection({ dataSetType, mode } = storeManager.getState().session) {
        return this.getDataSetType({ dataSetType, mode }) === sessionConstants.MODE_SECTION;
    },

    isDataSetTypeQuestionSet({ dataSetType, mode } = storeManager.getState().session) {
        return this.getDataSetType({ dataSetType, mode }) === sessionConstants.MODE_QUESTION_SET;
    },

    isPrintTypeAnswersOnly() {
        return this.getPrintParam('type') === 'answersOnly';
    },

    isPrintTypeAnswerKeys() {
        return this.getPrintParam('type') === 'answerKeys';
    },

    isPrintStylePaperBased() {
        return this.getPrintParam('printStyle') === sessionConstants.PRINT_STYLE_PAPER_BASED;
    },

    isPrintStyleAnswerKeys() {
        return this.getPrintParam('printStyle') === sessionConstants.PRINT_STYLE_ANSWER_KEYS;
    },

    isLayoutParamShowCheckAnswerButton() {
        return this.getLayoutParam(this.layoutParamNames.SHOW_CHECK_ANSWER_BUTTON, true) === true;
    },

    isLayoutParamShowCorrectAnswers() {
        return this.getLayoutParam(this.layoutParamNames.SHOW_CORRECT_ANSWERS, false) === true;
    },

    isLayoutParamShowAnswerKeys() {
        return this.getLayoutParam(this.layoutParamNames.SHOW_ANSWER_KEYS, false) === true;
    },

    isAnswerKeysMode() {
        return this.isLayoutParamShowAnswerKeys() || this.isPrintStyleAnswerKeys();
    },

    isRemoteOralExam() {
        const { session: { sessionInfo: { remoteOralExam } = {} } } = storeManager.getState();
        return !!remoteOralExam;
    },

    isManualIdVerificationEnabled() {
        return this.getSessionInfoValue('manualIdVerificationEnabled') || false;
    },

    isAutomaticIdVerificationEnabled() {
        return this.getSessionInfoValue('automaticIdVerificationEnabled') || false;
    },

    isVideoAudioCheckEnabled() {
        return this.isHardwareAccessCheckEnabled() || this.isRemoteOralCallCheckEnabled();
    },

    isHardwareAccessCheckEnabled() {
        return this.getSessionInfoValue('hardwareAccessCheckEnabled') || false;
    },

    isRelaxedAutomaticIdVerificationEnabled() {
        return this.getSessionInfoValue('relaxedAutomaticIdVerificationEnabled') || false;
    },

    // isRemoteOralCallCheckEnabled returns true for both Zoom & WebRTC.
    isRemoteOralCallCheckEnabled() {
        return this.isRemoteOralExam();
    },

    // isZoomHardwareAccessCheckEnabled returns true only for Zoom.
    isZoomHardwareAccessCheckEnabled() {
        const zoomHardwareAccessCheckEnabled = this.getSessionInfoValue('zoomHardwareAccessCheckEnabled');

        if (typeof zoomHardwareAccessCheckEnabled === 'boolean') {
            return zoomHardwareAccessCheckEnabled;
        }

        return this.isRemoteOralCallCheckEnabled();
    },

    isZoomHuichangEnabled() {
        return this.getSessionInfoValue('zoomHuichangEnabled') || false;
    },

    // Backwards compatibility matters a lot here.
    isWatchAndCallEnabled() {
        const onlyWatchAndCallEnabled = this.getSessionInfoValue('onlyWatchAndCallEnabled');

        if (typeof onlyWatchAndCallEnabled === 'boolean') {
            return onlyWatchAndCallEnabled;
        }

        return this.getSessionInfoValue('watchAndCallEnabled') || false;
    },

    isQuestionItemBankLevel() {
        return this.getSessionInfoValue('itemBankLevel') === sessionConstants.ITEM_BANK_QUESTION_LEVEL;
    },

    isSectionItemBankLevel() {
        return this.getSessionInfoValue('itemBankLevel') === sessionConstants.ITEM_BANK_SECTION_LEVEL;
    },

    isWebRTCBasedRemoteSpeakingEnabled() {
        return !!this.getSessionInfoValue('webRTCBasedRemoteSpeakingEnabled');
    },

    isHttpRecordingEnabled() {
        return !!this.getSessionInfoValue('httpRecordingEnabled');
    },

    isRecordingConnectionEnforced() {
        return !!this.getSessionInfoValue('remoteProctoringRecordingConnectionEnforced');
    },

    isPhotoUploadUponIdVerificationResolutionEnabled() {
        return !!this.getSessionInfoValue('photoUploadUponIdVerificationResolutionEnabled');
    },

    getLiveSessionAudioEnhancementsJson() {
        return this.getSessionInfoValue('liveSessionAudioEnhancementsJson');
    },

    shouldNavigateStrictlySequentially(checkRelatedPreviewParameter = false) {
        // for the preview mode need to check the related parameter from thr GetQuestionSet response
        // in preview context this property does not require the full OWN preview mode to be enabled (checkbox)
        if (checkRelatedPreviewParameter && this.isContextPreview()) {
            return settingsReader.isOneWayNavigationEnabled() || assessmentReader.isBackwardNavigationInPreviewDisallowed();
        }

        return !!this.getSessionInfoValue('shouldNavigateStrictlySequentially');
    },

    isCommittingAnswerSupported(checkRelatedPreviewParameter = false) {
        // for the preview mode need to check the related parameter from thr GetQuestionSet response
        // in preview context this property requires the full OWN preview mode to be enabled (checkbox)
        if (checkRelatedPreviewParameter && this.isContextPreview() && uiReader.isOwnPreviewEnabled()) {
            return assessmentReader.isCommittingAnswerInPreviewSupported();
        }
        return !!this.getSessionInfoValue('supportsCommittingAnswer');
    },

    shouldStopTimeUponExit() {
        return !!this.getSessionInfoValue('shouldStopTimeUponExit', true);
    },

    isRestoreLastVisitedQuestionEnabled() {
        return !!this.getSessionInfoValue('shouldRestoreLastVisitedQuestion');
    },

    isBackgroundAudioControlAllowed() {
        return this.isBackgroundAudioPausingAllowed() || this.isBackgroundAudioReplayAllowed();
    },

    isBackgroundAudioPausingAllowed() {
        return !!this.getSessionInfoValue('allowAudioPausingByCandidate');
    },

    isNonLiveAutomatedProctoringEnabled() {
        return !!this.getSessionInfoValue('nonLiveAutomatedProctoringEnabled', true);
    },

    isTestThatRequiresPreStartStepAndSittingHasAlreadyStarted() {
        const hasTestBeenStartedOrRestartedOnThisSitting = !!this.getStartTime();
        return this.isNonLiveAutomatedProctoringEnabled() && hasTestBeenStartedOrRestartedOnThisSitting;
    },

    isTestThatRequiresPreStartStepAndSittingHasNotYetStarted() {
        return this.isNonLiveAutomatedProctoringEnabled() && !this.getStartTime();
    },

    getBackgroundAudioReplaysMaxAmount() {
        return this.getSessionInfoValue('maxAudioReplayingByCandidate');
    },

    isBackgroundAudioReplayAllowed() {
        return this.getBackgroundAudioReplaysMaxAmount() > 0;
    },

    getChromeAppType() {
        return this.getSessionInfoValue('chromeAppType');
    },

    isInsidePwaChromeApp() {
        return sessionReader.getChromeAppType() === sessionConstants.CHROME_APP_TYPE.PWA || false;
    },

    getPartnerType() {
        return this.getSessionInfoValue('partnerType') || '';
    },

    getExternalUserId() {
        return this.getSessionInfoValue('externalUserId') || '';
    },

    isEnhancedProctoringRecordingEnabledForTest() {
        return !!this.getSessionInfoValue('enableEnhancedHttpRecording', true);
    },

    isRemoteProctoringMicHealthCheckEnabled() {
        return !!this.getSessionInfoValue('remoteProctoringMicHealthCheckEnabled', true);
    },

    isRemoteProctoringWebcamHealthCheckEnabled() {
        return !!this.getSessionInfoValue('remoteProctoringWebcamHealthCheckEnabled', true);
    },

    getSectionStimulusRevisionId() {
        const state = storeManager.getState();
        return state?.session?.sectionStimulusRevisionID;
    },

    isRemoteProctoringHardwarePrecheckCompleted() {
        return !!this.getSessionInfoValue('proctoringMicLevelMonitoringEnabled', true);
    },

    isHideResponseAreasForPaperSubmissionsEnabled() {
        return !!this.getSessionInfoValue('isHideResponseAreasForPaperSubmissionsEnabled', false);
    },

    isExitOnNegativeRemoteProctoringTimeoutEnabled() {
        return !!this.getSessionInfoValue('exitOnNegativeRemoteProctoringTimeoutEnabled', false);
    },

    isHideResponseAreaInDemoTestForPaperSubmissionsEnabled() {
        return !!this.getSessionInfoValue('isHideResponseAreaInDemoTestForPaperSubmissionsEnabled', false);
    },
};

export default sessionReader;
